import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ProductCard from "@/components/product/ProductCard";
import { useI18n } from "@/i18n/I18nProvider";

function SkeletonCard() {
  return (
    <div className="w-[220px] shrink-0 overflow-hidden rounded-2xl border border-border">
      <div className="aspect-square shimmer" />
      <div className="space-y-2 p-3">
        <div className="h-3 w-3/4 rounded shimmer" />
        <div className="h-3 w-1/2 rounded shimmer" />
        <div className="h-8 w-full rounded-full shimmer" />
      </div>
    </div>
  );
}

export default function ProductRail({ title, subtitle, products = [], loading, viewAllTo, accent, extra }) {
  const { t, isRtl } = useI18n();
  const ref = useRef(null);

  const scroll = (dir) => {
    if (!ref.current) return;
    const amount = 460 * (dir === "next" ? 1 : -1) * (isRtl ? -1 : 1);
    ref.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <section className="py-6" data-testid="product-rail">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {accent && <span className="h-6 w-1.5 rounded-full bg-brand" />}
            <h2 className="font-display text-xl font-bold text-ink sm:text-2xl">{title}</h2>
          </div>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {extra}
          {viewAllTo && (
            <Link to={viewAllTo} className="hidden text-sm font-semibold text-brand hover:underline sm:inline">
              {t("view_all")}
            </Link>
          )}
          <div className="hidden gap-1.5 sm:flex">
            <button onClick={() => scroll("prev")} className="grid h-9 w-9 place-items-center rounded-full border border-border hover:border-ink" aria-label="prev">
              <ChevronLeft size={18} className={isRtl ? "rotate-180" : ""} />
            </button>
            <button onClick={() => scroll("next")} className="grid h-9 w-9 place-items-center rounded-full border border-border hover:border-ink" aria-label="next">
              <ChevronRight size={18} className={isRtl ? "rotate-180" : ""} />
            </button>
          </div>
        </div>
      </div>

      <div ref={ref} className="no-scrollbar -mx-1 flex gap-4 overflow-x-auto scroll-smooth px-1 pb-2">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : products.map((p) => (
              <div key={p.id} className="w-[220px] shrink-0">
                <ProductCard product={p} />
              </div>
            ))}
      </div>
      {viewAllTo && (
        <Link to={viewAllTo} className="mt-2 inline-block text-sm font-semibold text-brand hover:underline sm:hidden">
          {t("view_all")}
        </Link>
      )}
    </section>
  );
}
